import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { ApiError } from "@/api/client";
import { useMe, useUpdateProfile } from "@/api/hooks";
import { Alert, PageHeader, Spinner, TextField } from "@/components/ui";
import { allZones, browserZone, formatDateTime } from "@/lib/time";

const LOCALES: [string, string][] = [
  ["", "Browser default"], ["en-IN", "English (India)"], ["en-US", "English (United States)"],
  ["en-GB", "English (United Kingdom)"], ["en-AU", "English (Australia)"], ["en-CA", "English (Canada)"],
  ["ar-AE", "العربية (الإمارات)"], ["de-DE", "Deutsch (Deutschland)"], ["fr-FR", "Français (France)"],
  ["es-ES", "Español (España)"], ["hi-IN", "हिन्दी (भारत)"],
];

export function ProfilePage() {
  const me = useMe();
  const update = useUpdateProfile();
  const zones = useMemo(allZones, []);
  const [name, setName] = useState("");
  const [zone, setZone] = useState("");
  const [locale, setLocale] = useState("");
  const [phone, setPhone] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ tone: "ok" | "danger"; text: string } | null>(null);
  // Saving a new zone re-mounts every page so all dates re-render; the URL keeps the confirmation.
  const [params, setParams] = useSearchParams();
  const saved = params.get("saved") === "1";

  useEffect(() => {
    if (!me.data) return;
    setName(me.data.user.full_name); setZone(me.data.user.timezone ?? ""); setLocale(me.data.user.locale ?? ""); setPhone(me.data.user.phone ?? "");
  }, [me.data]);

  if (!me.data) return <Spinner />;
  const effective = zone || browserZone();
  const sample = new Date().toISOString();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setErrors({}); setMessage(null); setParams({}, { replace: true });
    try {
      await update.mutateAsync({ full_name: name.trim(), timezone: zone || null, locale: locale || null, phone: phone.trim() || null });
      setParams({ saved: "1" }, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.problem.errors?.length) setErrors(err.fieldErrors());
      else setMessage({ tone: "danger", text: err instanceof Error ? err.message : "Could not save" });
    }
  };

  return (
    <>
      <PageHeader title="My profile" subtitle="Your name, time zone and date format. These apply only to you." />
      <form onSubmit={submit} className="card max-w-2xl space-y-4 p-5" noValidate>
        {message && <Alert tone={message.tone}>{message.text}</Alert>}
        {saved && !message && <Alert tone="ok">Saved. Dates and times now show in your time zone.</Alert>}
        <TextField id="full_name" label="Full name" value={name} onChange={(e) => setName(e.target.value)} error={errors.full_name} />
        <TextField id="email" label="Email" value={me.data.user.email} disabled hint="Ask an admin to change your sign-in email." />
        <TextField id="phone" label="Your phone" type="tel" placeholder="+91 98200 00000" value={phone} onChange={(e) => setPhone(e.target.value)} error={errors.phone}
          hint="Click-to-call rings this phone first, then connects you to the customer." />
        <div>
          <label htmlFor="tz" className="label">Time zone</label>
          <select id="tz" className="input" value={zone} onChange={(e) => setZone(e.target.value)} aria-invalid={!!errors.timezone}>
            <option value="">Use my browser's ({browserZone().replace(/_/g, " ")})</option>
            {zones.map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
          </select>
          {errors.timezone ? <p className="field-error">{errors.timezone}</p>
            : <p className="mt-1 text-xs text-muted">Everything is stored in UTC and shown to you in this zone. Your company's zone is {me.data.tenant.timezone.replace(/_/g, " ")}.</p>}
        </div>
        <div>
          <label htmlFor="locale" className="label">Date and number format</label>
          <select id="locale" className="input" value={locale} onChange={(e) => setLocale(e.target.value)}>
            {LOCALES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <p className="rounded-md bg-canvas px-3 py-2 text-sm text-muted" data-testid="time-preview">
          Right now it is <span className="font-medium text-ink">{previewTime(sample, effective, locale)}</span>
        </p>
        <div className="flex justify-end"><button type="submit" className="btn-primary" disabled={update.isPending}>Save profile</button></div>
      </form>
    </>
  );
}

function previewTime(iso: string, timeZone: string, locale: string): string {
  try {
    // dateStyle cannot be combined with timeZoneName, so spell the fields out.
    return new Intl.DateTimeFormat(locale || undefined, {
      weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "numeric", minute: "2-digit", timeZone, timeZoneName: "short",
    }).format(new Date(iso));
  } catch {
    return formatDateTime(iso);
  }
}
